import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import '../styles/ConclaveVirtuelPage.css';

// Composants SVG inline
const IconVoteYea = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="0">
    <path d="M23.5 12.8c-.2.4-.5.8-.9 1.1l-7 5.4c-.3.3-.7.3-1.1.3-.5 0-.9-.1-1.3-.4l-3-2.3L6 19.7c-.4.3-.8.4-1.3.4-.3 0-.7-.1-1-.3l-2.8-2.2c-.3-.2-.6-.5-.7-.9-.2-.4-.2-.8 0-1.2.2-.4.5-.8.9-1L3 13.7 1.8 12c-.3-.2-.5-.5-.7-.9-.1-.3-.1-.7 0-1 .2-.4.5-.8.9-1l2.8-2.2c.3-.2.7-.3 1-.3s.7.1 1 .3l4.2 3.4L13.9 8c.3-.2.7-.3 1-.3.4 0 .7.1 1 .3l2.8 2.2c.3.2.6.5.7.9.1.3.1.7 0 1-.1.3-.4.7-.7.9l-1.2 1 5.2-1.9c.4-.1.8-.1 1.2 0 .4.2.8.5 1 .9.1.3.1.7 0 1-.2.4-.5.7-.9.9l-.4.3 1.6-.2c.4 0 .8.1 1.1.3.3.2.6.5.7.9.3.4.3.8.1 1.1z"/>
  </svg>
);

const IconSmoke = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3C8 3 7 7 7 9s3 4 3 8c0 2-2 3-2 5 0 2 1 3 4 3s4-1 4-3c0-2-2-3-2-5 0-4 3-6 3-8s-1-6-5-6z" />
  </svg>
);

const IconHistory = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconThList = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="4" rx="1" />
    <rect x="3" y="13" width="18" height="4" rx="1" />
    <line x1="4" y1="11" x2="8" y2="11" />
    <line x1="4" y1="19" x2="8" y2="19" />
  </svg>
);

const IconThGrid = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const IconSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconFilter = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const IconInfo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ConclaveVirtuelPage = () => {
  const toast = useToast();
  
  // États principaux
  const [cardinaux, setCardinaux] = useState([]);
  const [filteredCardinaux, setFilteredCardinaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États de gestion du conclave
  const [conclaveInfo, setConclaveInfo] = useState(null);
  const [currentTour, setCurrentTour] = useState(1);
  const [resultatsConclave, setResultatsConclave] = useState([]);
  const [conclaveTermine, setConclaveTermine] = useState(false);
  const [papeElu, setPapeElu] = useState(null);
  
  // États de vote
  const [votesRestants, setVotesRestants] = useState(0);
  const [lastVoteTime, setLastVoteTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  // États d'interface
  const [activeTab, setActiveTab] = useState('simulation');
  const [viewMode, setViewMode] = useState(() => {
    const savedView = localStorage.getItem('conclaveViewMode');
    return savedView || 'list'; // Par défaut en liste
  });
  
  // États de filtrage et tri
  const [searchTerm, setSearchTerm] = useState('');
  const [continentFilter, setContinentFilter] = useState('');
  const [paysFilter, setPaysFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'votes', direction: 'desc' });
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  
  // Gestion des erreurs d'images
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback
  const defaultImage = '/images/cardinal-default.png';
  
  // Timestamp constant pour la session
  const sessionTimestamp = useMemo(() => Date.now(), []);
  
  // Fonction pour calculer le prochain vote
  const calculateNextVoteTime = useCallback(() => {
    if (!lastVoteTime) return null;
    return new Date(lastVoteTime.getTime() + 3600000); // +1 heure
  }, [lastVoteTime]);
  
  // Gestion du countdown optimisée
  useEffect(() => {
    if (!lastVoteTime) {
      setVotesRestants(1);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const nextVoteTime = calculateNextVoteTime();
      
      if (!nextVoteTime) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0 });
        setVotesRestants(1);
        return;
      }

      const timeDiff = nextVoteTime.getTime() - now.getTime();

      if (timeDiff <= 0) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0 });
        setVotesRestants(1);
      } else {
        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
        setTimeRemaining({ hours, minutes, seconds });
        setVotesRestants(0);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [lastVoteTime, calculateNextVoteTime]);
  
  // Fonction de chargement des données optimisée
  const fetchData = useCallback(async () => {
    try {
      // Récupérer l'état du conclave
      const stateResponse = await axios.get('/api/conclave/state');
      const { conclaveState, rounds } = stateResponse.data;
      
      setConclaveInfo(conclaveState);
      setCurrentTour(conclaveState.currentRound);
      setResultatsConclave(rounds);
      setConclaveTermine(conclaveState.isComplete);
      setPapeElu(conclaveState.popeElected);

      // Récupérer les cardinaux électeurs uniquement si nécessaire
      if (!cardinaux.length || cardinaux.length === 0) {
        const cardinauxResponse = await axios.get('/api/cardinaux/search?statutVotant=OUI&limit=120');
        
        // Récupérer les votes du tour actuel
        let currentVotes = {};
        if (!conclaveState.isComplete) {
          const votesResponse = await axios.get('/api/conclave/current-votes');
          votesResponse.data.results.forEach(result => {
            currentVotes[result.cardinal._id] = result.votes;
          });
        }
        
        // Initialiser les votes
        const cardinauxData = cardinauxResponse.data.cardinaux.map(cardinal => ({
          ...cardinal,
          votesConclave: currentVotes[cardinal._id] || 0
        }));
        
        setCardinaux(cardinauxData);
        setFilteredCardinaux([...cardinauxData]);
      } else {
        // Mettre à jour uniquement les votes
        if (!conclaveState.isComplete) {
          const votesResponse = await axios.get('/api/conclave/current-votes');
          let currentVotes = {};
          votesResponse.data.results.forEach(result => {
            currentVotes[result.cardinal._id] = result.votes;
          });
          
          const updatedCardinaux = cardinaux.map(cardinal => ({
            ...cardinal,
            votesConclave: currentVotes[cardinal._id] || 0
          }));
          
          setCardinaux(updatedCardinaux);
          setFilteredCardinaux(updatedCardinaux);
        }
      }
      
      // Vérifier le dernier vote de l'utilisateur
      if (!lastVoteTime) {
        const storedVotes = JSON.parse(localStorage.getItem('userVotes') || '[]');
        if (storedVotes.length > 0) {
          const lastVote = storedVotes[storedVotes.length - 1];
          setLastVoteTime(new Date(lastVote.time));
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Erreur lors du chargement des données:", err);
      setError("Erreur lors du chargement des données. Veuillez réessayer.");
      setLoading(false);
    }
  }, [cardinaux.length, lastVoteTime]);
  
  // Chargement initial et rafraîchissement optimisé
  useEffect(() => {
    fetchData();
    
    // Rafraîchir toutes les 2 minutes au lieu de 5
    const refreshInterval = setInterval(fetchData, 120000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchData]);

// Filtrage et tri optimisés
  useEffect(() => {
    let result = [...cardinaux];
    
    // Appliquer les filtres
    if (searchTerm) {
      result = result.filter(cardinal => 
        cardinal.nomComplet.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cardinal.titre?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (continentFilter) {
      result = result.filter(cardinal => cardinal.continent === continentFilter);
    }
    
    if (paysFilter) {
      result = result.filter(cardinal => cardinal.paysService === paysFilter || cardinal.paysNaissance === paysFilter);
    }
    
    // Appliquer le tri
    result.sort((a, b) => {
      if (sortConfig.key === 'votes') {
        return sortConfig.direction === 'asc' 
          ? (a.votesConclave || 0) - (b.votesConclave || 0)
          : (b.votesConclave || 0) - (a.votesConclave || 0);
      } else if (sortConfig.key === 'nom') {
        return sortConfig.direction === 'asc'
          ? a.nomComplet.localeCompare(b.nomComplet)
          : b.nomComplet.localeCompare(a.nomComplet);
      } else if (sortConfig.key === 'age') {
        return sortConfig.direction === 'asc'
          ? a.age - b.age
          : b.age - a.age;
      }
      return 0;
    });
    
    setFilteredCardinaux(result);
  }, [cardinaux, searchTerm, continentFilter, paysFilter, sortConfig]);
  
  // Fonction de vote optimisée
  const voteForCardinal = useCallback(async (cardinalId) => {
    if (!conclaveInfo || !conclaveInfo.isActive || conclaveInfo.isPaused) {
      toast.warning("Le vote est actuellement fermé.");
      return;
    }

    const now = new Date();
    
    if (lastVoteTime) {
      const timeSinceLastVote = now.getTime() - lastVoteTime.getTime();
      
      if (timeSinceLastVote < 3600000) {
        const minutesRemaining = Math.ceil((3600000 - timeSinceLastVote) / 60000);
        toast.warning(`Vous devez attendre encore ${minutesRemaining} minute(s) avant de pouvoir voter à nouveau.`);
        return;
      }
    }
    
    try {
      const response = await axios.post('/api/conclave/vote', { cardinalId });
      
      const newVote = {
        cardinalId,
        time: now.toISOString(),
        tour: currentTour
      };
      
      const storedVotes = JSON.parse(localStorage.getItem('userVotes') || '[]');
      const updatedVotes = [...storedVotes, newVote];
      localStorage.setItem('userVotes', JSON.stringify(updatedVotes));
      
      setLastVoteTime(now);
      setVotesRestants(0);
      
      // Mise à jour optimiste des votes
      setCardinaux(prevCardinaux => 
        prevCardinaux.map(cardinal => 
          cardinal._id === cardinalId 
          ? { ...cardinal, votesConclave: (cardinal.votesConclave || 0) + 1 } 
          : cardinal
        )
      );
      
      const cardinal = cardinaux.find(c => c._id === cardinalId);
      toast.success(`Merci d'avoir voté pour ${cardinal.nomComplet}`);
      
      // Rafraîchir les données après 2 secondes
      setTimeout(fetchData, 2000);
      
    } catch (error) {
      console.error("Erreur lors du vote:", error);
      toast.error(error.response?.data?.message || "Erreur lors de l'enregistrement de votre vote. Veuillez réessayer.");
    }
  }, [conclaveInfo, lastVoteTime, currentTour, cardinaux, toast, fetchData]);
  
  // Gestion optimisée des URLs d'images
  const getApiImageUrl = useCallback((originalUrl, cardinalId) => {
    if (!originalUrl || imageErrors[cardinalId]) return defaultImage;
    
    const filename = originalUrl.split('/').pop().split('?')[0];
    return `/api/images/${filename}?t=${sessionTimestamp}`;
  }, [imageErrors, sessionTimestamp]);
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = useCallback((cardinalId) => {
    if (!imageErrors[cardinalId]) {
      console.log(`Erreur de chargement d'image pour cardinal ${cardinalId}, utilisation de l'image par défaut`);
      setImageErrors(prev => ({
        ...prev,
        [cardinalId]: true
      }));
    }
  }, [imageErrors]);
  
  // Changement de mode d'affichage
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    localStorage.setItem('conclaveViewMode', mode);
  }, []);
  
  // Fonctions utilitaires
  const formatDate = (dateString) => {
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  const getUniquePays = () => {
    const pays = new Set();
    cardinaux.forEach(cardinal => {
      if (cardinal.paysService) pays.add(cardinal.paysService);
      if (cardinal.paysNaissance) pays.add(cardinal.paysNaissance);
    });
    return Array.from(pays).sort();
  };
  
  const resetFilters = () => {
    setSearchTerm('');
    setContinentFilter('');
    setPaysFilter('');
    setSortConfig({ key: 'votes', direction: 'desc' });
  };

  const getTopThreeCardinaux = () => {
    return [...cardinaux]
      .sort((a, b) => (b.votesConclave || 0) - (a.votesConclave || 0))
      .slice(0, 3);
  };

  if (loading) return <div className="loading">Chargement du conclave virtuel...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!conclaveInfo) return <div className="loading">Chargement...</div>;

  const continents = ['Afrique', 'Amérique', 'Asie', 'Europe', 'Océanie'];
  const paysList = getUniquePays();

  return (
    <div className="conclave-page">
      <div className="conclave-header">
        <h1>Conclave Virtuel 2025</h1>
        <p className="subtitle">Participez à la simulation du prochain conclave</p>
      </div>

      {/* Nouvelle structure avec layout optimisé */}
      <div className="conclave-main-layout">
        {/* Colonne principale */}
        <div className="conclave-main-content">
          {/* Podium en premier */}
          {!conclaveTermine && cardinaux.some(c => c.votesConclave > 0) && (
            <div className="cardinaux-podium">
              <h2>Podium du Tour {currentTour}</h2>
              <div className="podium-cards">
                {getTopThreeCardinaux().map((cardinal, index) => (
                  <div key={cardinal._id} className={`podium-card rank-${index + 1}`}>
                    <div className="podium-rank">{index + 1}</div>
                    <img 
                      src={getApiImageUrl(cardinal.photo, cardinal._id)} 
                      alt={cardinal.nomComplet}
                      onError={() => handleImageError(cardinal._id)}
                    />
                    <h3>{cardinal.nomComplet}</h3>
                    <p>{cardinal.paysService}</p>
                    <div className="podium-votes">
                      {cardinal.votesConclave || 0} votes
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="conclave-tabs">
            <button 
              className={`tab-button ${activeTab === 'simulation' ? 'active' : ''}`}
              onClick={() => setActiveTab('simulation')}
            >
              Simulation du Conclave
            </button>
            <button 
              className={`tab-button ${activeTab === 'explication' ? 'active' : ''}`}
              onClick={() => setActiveTab('explication')}
            >
              Comment fonctionne le Conclave ?
            </button>
            <button 
              className={`tab-button ${activeTab === 'historique' ? 'active' : ''}`}
              onClick={() => setActiveTab('historique')}
            >
              <IconHistory /> Historique des Tours
            </button>
          </div>

          <div className="conclave-content">
            {activeTab === 'simulation' && (
              <div className="conclave-simulation">
                {!conclaveTermine ? (
                  <>
                    <h2>Votez pour le prochain Pape</h2>
                    
                    {/* Status de vote intégré */}
                    <div className={`votes-info ${votesRestants > 0 ? 'can-vote' : ''}`}>
                      {conclaveInfo.isPaused ? (
                        <div className="pause-notification">
                          <IconInfo className="info-icon" />
                          <div>
                            <p className="pause-title">⏸️ Conclave en pause</p>
                            <p>{conclaveInfo.pauseReason}</p>
                            {conclaveInfo.pauseExhortationBy && (
                              <p>Exhortation par : {conclaveInfo.pauseExhortationBy}</p>
                            )}
                          </div>
                        </div>
                      ) : votesRestants === 0 && lastVoteTime ? (
                        <>
                          <p>Vous pourrez voter dans :</p>
                          <div className="countdown-inline">
                            <span className="countdown-item">
                              <span className="countdown-number">{timeRemaining.hours.toString().padStart(2, '0')}</span>
                              <span className="countdown-text">h</span>
                            </span>
                            <span className="countdown-separator">:</span>
                            <span className="countdown-item">
                              <span className="countdown-number">{timeRemaining.minutes.toString().padStart(2, '0')}</span>
                              <span className="countdown-text">m</span>
                            </span>
                            <span className="countdown-separator">:</span>
                            <span className="countdown-item">
                              <span className="countdown-number">{timeRemaining.seconds.toString().padStart(2, '0')}</span>
                              <span className="countdown-text">s</span>
                            </span>
                          </div>
                        </>
                      ) : (
                        <p>🗳️ Vous pouvez voter maintenant !</p>
                      )}
                    </div>
                    
                    <div className="cardinaux-filters">
                      <div className="filter-toggle-mobile" onClick={() => setShowFiltersMobile(!showFiltersMobile)}>
                        <IconFilter /> Filtres et tri
                      </div>
                      
                      <div className={`filters-container ${showFiltersMobile ? 'show' : ''}`}>
                        <div className="search-bar">
                          <IconSearch className="search-icon" />
                          <input
                            type="text"
                            placeholder="Rechercher un cardinal..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          {searchTerm && (
                            <button 
                              className="clear-search" 
                              onClick={() => setSearchTerm('')}
                              aria-label="Effacer la recherche"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        
                        <div className="filter-options">
                          <select
                            value={continentFilter}
                            onChange={(e) => setContinentFilter(e.target.value)}
                            className="filter-select"
                          >
                            <option value="">Tous les continents</option>
                            {continents.map(continent => (
                              <option key={continent} value={continent}>{continent}</option>
                            ))}
                          </select>
                          
                          <select
                            value={paysFilter}
                            onChange={(e) => setPaysFilter(e.target.value)}
                            className="filter-select"
                          >
                            <option value="">Tous les pays</option>
                            {paysList.map(pays => (
                              <option key={pays} value={pays}>{pays}</option>
                            ))}
                          </select>
                          
                          <select
                            value={`${sortConfig.key}-${sortConfig.direction}`}
                            onChange={(e) => {
                              const [key, direction] = e.target.value.split('-');
                              setSortConfig({ key, direction });
                            }}
                            className="filter-select"
                          >
                            <option value="votes-desc">Votes: décroissant</option>
                            <option value="votes-asc">Votes: croissant</option>
                            <option value="nom-asc">Nom: A-Z</option>
                            <option value="nom-desc">Nom: Z-A</option>
                            <option value="age-desc">Âge: décroissant</option>
                            <option value="age-asc">Âge: croissant</option>
                          </select>
                          
                          <button 
                            className="reset-filters-button"
                            onClick={resetFilters}
                            title="Réinitialiser les filtres"
                          >
                            Réinitialiser
                          </button>
                        </div>
                      </div>
                      
                      <div className="view-mode-toggle">
                        <button
                          className={`view-mode-button ${viewMode === 'list' ? 'active' : ''}`}
                          onClick={() => handleViewModeChange('list')}
                          title="Vue en liste"
                        >
                          <IconThList />
                        </button>
                        <button
                          className={`view-mode-button ${viewMode === 'grid' ? 'active' : ''}`}
                          onClick={() => handleViewModeChange('grid')}
                          title="Vue en grille"
                        >
                          <IconThGrid />
                        </button>
                      </div>
                    </div>
				{filteredCardinaux.length === 0 ? (
                      <p className="no-results">Aucun cardinal ne correspond à vos critères de recherche</p>
                    ) : (
                      viewMode === 'list' ? (
                        <div className="cardinaux-voting-list">
                          {filteredCardinaux.map(cardinal => (
                            <div key={cardinal._id} className="cardinal-voting-row">
                              <div className="cardinal-voting-row-content">
                                <div className="cardinal-voting-photo">
                                  <img 
                                    src={getApiImageUrl(cardinal.photo, cardinal._id)} 
                                    alt={cardinal.nomComplet} 
                                    onError={() => handleImageError(cardinal._id)}
                                  />
                                </div>
                                <div className="cardinal-voting-details">
                                  <h3>{cardinal.nomComplet}</h3>
                                  <div className="cardinal-info-grid">
                                    <span><strong>Âge:</strong> {cardinal.age} ans</span>
                                    <span><strong>Pays:</strong> {cardinal.paysService}</span>
                                    <span><strong>Continent:</strong> {cardinal.continent}</span>
                                    <span><strong>Fonction:</strong> {cardinal.fonction || cardinal.titre || "Non spécifiée"}</span>
                                  </div>
                                </div>
                                <div className="cardinal-voting-stats">
                                  <div className="vote-count">{cardinal.votesConclave || 0} vote{(cardinal.votesConclave || 0) > 1 ? 's' : ''}</div>
                                  <button 
                                    className={`vote-button ${votesRestants === 0 ? 'disabled' : ''}`}
                                    onClick={() => voteForCardinal(cardinal._id)}
                                    disabled={votesRestants === 0 || conclaveInfo.isPaused}
                                  >
                                    Voter
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="cardinaux-voting-grid">
                          {filteredCardinaux.map(cardinal => (
                            <div key={cardinal._id} className="cardinal-voting-card">
                              <img 
                                src={getApiImageUrl(cardinal.photo, cardinal._id)} 
                                alt={cardinal.nomComplet} 
                                onError={() => handleImageError(cardinal._id)}
                              />
                              <div className="cardinal-voting-info">
                                <h3>{cardinal.nomComplet}</h3>
                                <p>{cardinal.age} ans · {cardinal.paysService}</p>
                                <p className="cardinal-continent">{cardinal.continent}</p>
                                <div className="cardinal-votes-count">
                                  <span>{cardinal.votesConclave || 0} vote{(cardinal.votesConclave || 0) > 1 ? 's' : ''}</span>
                                </div>
                              </div>
                              <button 
                                className={`vote-button ${votesRestants === 0 ? 'disabled' : ''}`}
                                onClick={() => voteForCardinal(cardinal._id)}
                                disabled={votesRestants === 0 || conclaveInfo.isPaused}
                              >
                                Voter
                              </button>
                            </div>
                          ))}
                        </div>
                      )
                    )}
                    
                    <div className="results-info">
                      <p>Nombre de cardinaux affichés: {filteredCardinaux.length} sur {cardinaux.length}</p>
                      <p>Majorité requise pour l'élection: <strong>{Math.ceil(conclaveInfo.electors * 2/3)}</strong> votes</p>
                    </div>
                  </>
                ) : (
                  <div className="conclave-results">
                    <h2>Résultats du Conclave Virtuel</h2>
                    <div className="elected-pope">
                      <div className="pope-card">
                        <img 
                          src={getApiImageUrl(papeElu?.photo, papeElu?._id)} 
                          alt={papeElu?.nomComplet} 
                          onError={() => handleImageError(papeElu?._id)}
                          className="pope-photo-large"
                        />
                        <div className="pope-card-content">
                          <h3>Habemus Papam!</h3>
                          <h4>{papeElu?.nomComplet}</h4>
                          <p>Élu le {formatDate(new Date())} au {currentTour}e tour</p>
                        </div>
                      </div>
                    </div>
                    
                    <h3>Classement final des cardinaux</h3>
                    <div className="final-results-grid">
                      {filteredCardinaux.slice(0, 10).map((cardinal, index) => (
                        <div key={cardinal._id} className="final-result-card">
                          <div className="rank-badge">{index + 1}</div>
                          <img 
                            src={getApiImageUrl(cardinal.photo, cardinal._id)} 
                            alt={cardinal.nomComplet} 
                            onError={() => handleImageError(cardinal._id)}
                          />
                          <h4>{cardinal.nomComplet}</h4>
                          <p>{cardinal.votesConclave || 0} vote{(cardinal.votesConclave || 0) > 1 ? 's' : ''}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'explication' && (
              <div className="conclave-explanation">
                <h2>Comment fonctionne un Conclave ?</h2>
                
                {/* Contenu existant */}
                <div className="explanation-section">
                  <h3>Qu'est-ce qu'un conclave ?</h3>
                  <p>
                    Le conclave est la réunion des cardinaux électeurs (de moins de 80 ans) qui se rassemblent
                    dans la Chapelle Sixtine pour élire un nouveau pape. Le terme "conclave" vient du latin
                    "cum clave" (avec clé), car les cardinaux sont enfermés à clé jusqu'à ce qu'ils aient élu un pape.
                  </p>
                </div>
                
                {/* Reste du contenu des explications */}
              </div>
            )}

            {activeTab === 'historique' && (
              <div className="conclave-history">
                <h2>Historique des Tours</h2>
                
                {resultatsConclave && resultatsConclave.length > 0 ? (
                  <div className="history-timeline">
                    {resultatsConclave.map((tour, index) => (
                      <div key={index} className="history-entry">
                        <div className="history-date">
                          <span className="round-number">Tour {tour.roundNumber}</span>
                          <span className="round-date">{formatDate(tour.timestamp)}</span>
                        </div>
                        <div className="history-card">
                          <div className={`smoke-indicator ${tour.whiteSmoke ? 'white-smoke' : 'black-smoke'}`}>
                            <div className="smoke-icon">
                              <IconSmoke />
                            </div>
                            <span>{tour.whiteSmoke ? 'Fumée blanche' : 'Fumée noire'}</span>
                          </div>
                          
                          <h3>Résultats du tour {tour.roundNumber}</h3>
                          
                          {tour.results && tour.results.length > 0 ? (
                            <div className="round-results">
                              <table className="results-table">
                                <thead>
                                  <tr>
                                    <th>Rang</th>
                                    <th>Cardinal</th>
                                    <th>Votes</th>
                                    <th>Pourcentage</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {tour.results.slice(0, 5).map((result, idx) => (
                                    <tr key={idx}>
                                      <td>{idx + 1}</td>
                                      <td>{result.cardinalId?.nomComplet || 'Inconnu'}</td>
                                      <td>{result.votes}</td>
                                      <td>{result.percentage}%</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="no-results-message">Aucun vote n'a été enregistré pour ce tour.</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-history">Aucun historique disponible pour l'instant.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar avec statut du conclave */}
        <div className="conclave-sidebar">
          <div className="conclave-status">
            {conclaveTermine ? (
              <div className="conclave-complete">
                <h3>Conclave terminé</h3>
                <div className="pope-elected">
                  <img 
                    src={getApiImageUrl(papeElu?.photo, papeElu?._id)} 
                    alt={papeElu?.nomComplet}
                    onError={() => handleImageError(papeElu?._id)} 
                    className="pope-photo"
                  />
                  <div className="pope-info">
                    <h4>Habemus Papam!</h4>
                    <p className="pope-name">{papeElu?.nomComplet}</p>
                    <p className="pope-elected-date">Élu le {formatDate(new Date())}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="conclave-active">
                <h3>Conclave en cours</h3>
                <div className="current-round">
                  <p>Jour: <strong>{conclaveInfo.currentDay}</strong></p>
                  <p>Période: <strong>{conclaveInfo.dayPeriod === 'morning' ? 'Matin' : 'Après-midi'}</strong></p>
                  <p>Tour actuel: <strong>{currentTour}</strong></p>
                </div>
                
                <div className={`smoke-status ${conclaveInfo.whiteSmoke ? 'white' : 'black'}`}>
                  <div className="smoke-animation">
                    <IconSmoke className="smoke-icon" />
                    <div className="smoke-particles">
                      {[...Array(5)].map((_, i) => (
                        <div 
                          key={i} 
                          className="smoke-particle"
                          style={{
                            left: `${20 + i * 15}%`,
                            width: `${8 + Math.random() * 10}px`,
                            height: `${8 + Math.random() * 10}px`,
                            animationDelay: `${i * 0.5}s`
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <span>{conclaveInfo.whiteSmoke ? 'Fumée blanche' : 'Fumée noire'}</span>
                  <p className="smoke-explanation">
                    {conclaveInfo.whiteSmoke 
                      ? "Un pape a été élu ! Habemus Papam !" 
                      : "Aucun cardinal n'a encore atteint la majorité requise des deux tiers."}
                  </p>
                </div>
              </div>
            )}
            
            {/* Introduction déplacée ici */}
            <div className="intro-text">
              <h3>Qu'est-ce que le Conclave Virtuel ?</h3>
              <p>
                Le Conclave Virtuel est une simulation permettant aux utilisateurs du site de 
                "voter" comme s'ils étaient cardinaux électeurs lors d'un conclave.
              </p>
              <p>
                Vous pouvez voter <strong>1 fois par heure</strong>. Les résultats sont 
                compilés et mis à jour en temps réel.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConclaveVirtuelPage;